import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, Card, Chip, Column, DataRow, Row, Typography,
} from '@tempmee/react';

/** What the ticket wants shown when a rule does not constrain a dimension. */
export const ALL_TARGETS = 'All';

const joinOrAll = (values) => (values.length > 0 ? values.join(', ') : ALL_TARGETS);

/** The wrapping row of chips the Metro and DSO readouts share. */
const ChipStrip = ({ chips }) => (
  <Row gap={2} wrap="wrap" pt={1}>
    {chips.map((chip) => (
      <Chip key={chip.key} label={chip.label} />
    ))}
  </Row>
);

ChipStrip.propTypes = {
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

/**
 * The "Targeting" card: who the code is for, as up to six readouts.
 *
 * Profession types and states are short codes and read fine as a list. DSOs
 * are stored on the rule as codes, which nobody in ops knows by heart, so the
 * page resolves them to names (via `useDsoNameResolver`) and passes them here
 * already resolved, as `{ code, name }` chips — this card holds no store
 * access of its own, so it renders from props alone.
 *
 * Type (posting types, already labelled) and Metro (RAM ids resolved to names
 * by the mapper) render only when the rule constrains them: no such leaf means
 * no row at all, not "All".
 *
 * Office List (GOTU-9810) is the same: it renders only when the rule names
 * offices by id, and then only as a way into the viewer — a rule can name up
 * to 10,000 of them, far past what a readout can hold. The page owns the
 * viewer; the card only asks for it.
 */
export const PromoCodeTargetingCard = ({
  professions, postingTypes, states, metros, dsos, onViewOffices,
}) => (
  <Card outline className="flex-1 min-w-0">
    <Column px={5} py={4} gap={2}>
      <Typography variant="subtitle1" as="h2" bold className="text-text-secondary">
        Targeting
      </Typography>

      <DataRow label="Profession type" orientation="stacked" value={joinOrAll(professions)} />
      {postingTypes.length > 0 && (
        <DataRow label="Type" orientation="stacked" value={postingTypes.join(', ')} />
      )}
      <DataRow label="State" orientation="stacked" value={joinOrAll(states)} />
      {metros.length > 0 && (
        <DataRow
          label="Metro"
          orientation="stacked"
          value={(
            <ChipStrip chips={metros.map((metro) => ({ key: metro.id, label: metro.name }))} />
          )}
        />
      )}
      <DataRow
        label="DSO"
        orientation="stacked"
        divider={false}
        value={
          dsos.length > 0 ? (
            <ChipStrip chips={dsos.map((dso) => ({ key: dso.code, label: dso.name }))} />
          ) : (
            ALL_TARGETS
          )
        }
      />
      {onViewOffices && (
        <DataRow
          label="Office List"
          orientation="stacked"
          divider={false}
          value={(
            <Button variant="text" size="small" onClick={onViewOffices} aria-label="View office list">
              View
            </Button>
          )}
        />
      )}
    </Column>
  </Card>
);

PromoCodeTargetingCard.propTypes = {
  professions: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Posting-type labels ("Interviews", "Shifts"); empty hides the Type row. */
  postingTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  states: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Metro targets, resolved to display names by the mapper; empty hides the Metro row. */
  metros: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /** DSO targets, resolved to display names by the page. */
  dsos: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /** Opens the office viewer; omitted when the rule names no offices, which hides the row. */
  onViewOffices: PropTypes.func,
};

PromoCodeTargetingCard.defaultProps = {
  onViewOffices: null,
};

export default PromoCodeTargetingCard;
